import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { getLoggedUser } from '@/features/user/api';
import { useAuth } from '@/features/auth';
import {
  isReauthenticationError,
  isWrongCredentialsError,
  redirectToSessionEnded,
  safePostLoginHref,
} from '@/features/auth/utils';
import { LoadingState } from '@/ui/components/loading-state';
import { toastManager } from '@/ui/components/toast';
import * as React from 'react';
import * as Sentry from '@sentry/react';
import { z } from 'zod';
import { SetLocaleContext } from '@/features/i18n/context';
import { t } from '@/features/i18n/utils';
import { Button } from '@/ui/components/button';
import { dateFormat, formatDate } from '@/dates';

const SSO_RETRY_KEY = 'auth0_sso_retry';

export const Route = createFileRoute('/_auth/auth0/callback')({
  component: RouteComponent,
  validateSearch: z.object({
    code: z.string().optional(),
    state: z.string().optional(),
    error: z.string().optional(),
    error_description: z.string().optional(),
  }),
});

function RouteComponent() {
  const { handleRedirectCallback, getAccessTokenSilently } = useAuth0();
  const { login, isLoading } = useAuth();
  const setLocale = React.use(SetLocaleContext);
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const callbackHandled = React.useRef(false);

  React.useEffect(() => {
    const fail = (e: unknown) => {
      if (isReauthenticationError(e)) return redirectToSessionEnded('sessionExpired');
      setErrorMessage(
        isWrongCredentialsError(e)
          ? t({ en: 'Wrong email or password', nb: 'Feil e-post eller passord' })
          : t({
              en: 'Authentication failed. Please try again.',
              nb: 'Autentisering feilet. Vennligst prøv igjen.',
            }),
      );
      Sentry.captureException(e);
    };

    const redirect = async (returnTo?: string) => {
      try {
        const token = await getAccessTokenSilently();
        const { data: loggedUser } = await getLoggedUser(token);

        if (loggedUser.systemLanguage) {
          setLocale(loggedUser.systemLanguage);
        }

        if (loggedUser.previousLoginAt) {
          toastManager.add({
            type: 'info',
            title: t({
              en: `Previous sign-in: ${formatDate(
                loggedUser.previousLoginAt,
                dateFormat.withTime,
                loggedUser.systemLanguage || 'en-US',
              )}`,
              nb: `Forrige pålogging: ${formatDate(
                loggedUser.previousLoginAt,
                dateFormat.withTime,
                loggedUser.systemLanguage || 'en-US',
              )}`,
            }),
          });
        }

        navigate({ href: safePostLoginHref(returnTo) });
      } catch (e) {
        fail(e);
      }
    };

    const handleCallback = async () => {
      if (callbackHandled.current) return;
      callbackHandled.current = true;
      sessionStorage.removeItem(SSO_RETRY_KEY);

      try {
        const result = await handleRedirectCallback();
        const {
          returnTo: returnToUrl,
          activationSuccess,
          organizationName,
        } = result?.appState ?? {};

        navigate({ to: '.', search: {} });

        if (activationSuccess) {
          toastManager.add({
            type: 'success',
            title: t({ en: 'Account activated successfully!', nb: 'Konto aktivert!' }),
            timeout: Infinity,
          });
        }
        if (organizationName) {
          toastManager.add({
            type: 'success',
            title: t({ en: 'Successfully joined', nb: 'Vellykket tilkobling' }),
            description: t({
              en: (
                <>
                  You are now a member of <strong>{organizationName}</strong> organisation
                </>
              ),
              nb: (
                <>
                  Du er nå medlem av organisasjonen <strong>{organizationName}</strong>
                </>
              ),
            }),
            timeout: Infinity,
          });
        }

        await redirect(returnToUrl);
      } catch (e) {
        fail(e);
      }
    };

    if (search.code && search.state) {
      handleCallback();
    } else if (!isLoading && !callbackHandled.current) {
      if (search.error) {
        callbackHandled.current = true;
        if (search.error_description === 'email_not_verified') {
          navigate({ to: '/activate-account' });
        } else if (search.error_description === 'sso_required') {
          if (sessionStorage.getItem(SSO_RETRY_KEY)) {
            sessionStorage.removeItem(SSO_RETRY_KEY);
            setErrorMessage(
              t({
                en: 'Your organization requires single sign-on. Please sign in with SSO.',
                nb: 'Organisasjonen din krever enkeltpålogging. Vennligst logg inn med SSO.',
              }),
            );
          } else {
            sessionStorage.setItem(SSO_RETRY_KEY, '1');
            login({ connection: import.meta.env.VITE_AUTH0_SSO_CONNECTION });
          }
        } else if (
          search.error_description === 'not_invited' ||
          search.error_description === 'wrong_tenant'
        ) {
          toastManager.add({
            type: 'error',
            title: t({ en: 'Access denied', nb: 'Tilgang nektet' }),
            description:
              search.error_description === 'not_invited'
                ? t({
                    en: "You haven't been invited to an organization. Please contact your administrator.",
                    nb: 'Du har ikke blitt invitert til en organisasjon. Kontakt administratoren din.',
                  })
                : t({
                    en: "We couldn't verify your organization's single sign-on. Please contact your administrator.",
                    nb: 'Vi kunne ikke bekrefte organisasjonens enkeltpålogging (SSO). Kontakt administratoren din.',
                  }),
            timeout: Infinity,
          });
          navigate({ to: '/login', search: {} });
        } else if (isWrongCredentialsError(search)) {
          setErrorMessage(t({ en: 'Wrong email or password', nb: 'Feil e-post eller passord' }));
        } else {
          setErrorMessage(
            t({
              en: 'Authentication failed. Please try again.',
              nb: 'Autentisering feilet. Vennligst prøv igjen.',
            }),
          );
        }
      } else {
        redirect();
      }
    }
  }, [
    handleRedirectCallback,
    getAccessTokenSilently,
    login,
    navigate,
    search,
    isLoading,
    setLocale,
  ]);

  if (errorMessage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen w-full">
        <p className="text-destructive mb-4">{errorMessage}</p>
        <Button
          nativeButton={false}
          render={<Link to="/login" />}
          variant="link"
          className="text-sm!"
        >
          {t({ en: 'Go to login', nb: 'Gå til innlogging' })}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full">
      <LoadingState context="auth" />
    </div>
  );
}
